import React, { useState } from 'react';
import { useParams } from 'react-router';
import { NavLink } from 'react-router-dom';

import { DateTime } from 'luxon';
import cn from 'classnames';

import { Breadcrumb } from '@shared/components/bootstrap';
import { Lightbox, Picture } from '@shared/components/helpers';
import { UserRole } from '@shared/types/user_role';
import { ImgixBG, ImgixFit } from '@shared/utils/imgix';

import { BarcodeValue } from '@admin/components/barcode/barcode_value';
import { Formatter as CategoryFormatter } from '@admin/components/categories/formatter';
import { Label } from '@admin/components/helpers/label';
import { PageHeader } from '@admin/components/helpers/page_header';
import { Panel } from '@admin/components/helpers/panel';
import { Roles } from '@admin/components/helpers/roles';
import { HistoriesPanel } from '@admin/components/histories/panel';
import { IncidentsPanel } from '@admin/components/incidents/panel';
import { ItemExceptionsPanel } from '@admin/components/item_exceptions/panel';
import { NonexistentParts } from '@admin/components/item_groups/nonexistent_parts';
import { ItemGroupsPanel as ItemGroupPanel } from '@admin/components/item_groups/panel';
import { AttachedItems } from '@admin/components/items/attached_items';
import { NotesPanel } from '@admin/components/notes/panel';
import { OpsAuditResultsPanel } from '@admin/components/ops_audit/audit_results/audit_results_panel';
import { List as OrdersList } from '@admin/components/orders/list';
import { QaQrCodeLink } from '@admin/components/qa_automation/qa_qr_code_link';
import { Spinner } from '@admin/components/spinner';
import { CreateTicket as CreateTechSupportTicket } from '@admin/components/tickets/tech_support/create_ticket';
import { Panel as TimelineActivityPanel } from '@admin/components/timeline_activity/panel';
import { useTitle } from '@admin/hooks/use_title';
import { client } from '@admin/libraries/apollo';
import {
  useItemDetailsQuery,
  Ticket__TicketableEnum,
  OrderSubject,
  History__Subject,
  NoteNotableEnum,
  ImageState,
  type Image,
  type ItemDetailsQuery,
  useItemHeroUpdateMutation,
} from '@admin/schema';

import { ItemActivateButton } from './item_activate_button';
import { ItemDeactivateButton } from './item_deactivate_button';
import { ItemManualReturnButton } from './item_manual_return_button';
import { ItemState } from './item_state';

const ItemHeroCheckbox: React.FC<{
  imageId: string;
  item: NonNullable<ItemDetailsQuery['item']>;
}> = ({ imageId, item }) => {
  const checked = item.heroImageID === imageId;
  const [execute] = useItemHeroUpdateMutation({ client });
  return (
    <input
      aria-label="Hero"
      type="radio"
      name="item[hero]"
      value={imageId}
      checked={checked}
      onChange={async (event) => {
        event.preventDefault();
        event.stopPropagation();
        try {
          const response = await execute({ variables: { id: item.id, heroImageID: imageId } });
          const error = response.data?.itemHeroUpdate?.error;
          if (error) throw new Error(error);
        } catch (error) {
          alert(String(error));
        }
      }}
    />
  );
};

const ItemImagePanel: React.FC<{
  image: NonNullable<ItemDetailsQuery['item']>['images'][number];
  index: number;
  total: number;
  item?: NonNullable<ItemDetailsQuery['item']>;
  showHeroCheckbox?: boolean;
  onSelect(image: Pick<Image, 'id' | 'source' | 'state'>): void;
}> = ({ image, index, total, item, showHeroCheckbox = false, onSelect }) => (
  <div className="col-xs-6 col-sm-4 col-md-3 col-lg-2">
    <Panel className={cn('brick', `image-${image.id}`)}>
      <a
        href="#"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (image.state !== ImageState.Uploaded) return;
          onSelect(image);
        }}
      >
        <figure className="brick-square">
          <Picture
            alt={`Photo #${index + 1} of ${total}`}
            className="brick-image"
            source={image.state === ImageState.Uploaded ? image.source : undefined}
            width={480}
            height={480}
            fit={ImgixFit.Crop}
            background={ImgixBG.White}
          />
        </figure>
      </a>
      <Panel.Footer>
        <div className="text-center">
          {image.name && <div>{image.name}</div>}
          {image.user && <div>Taken by: {image.user.name}</div>}
          <div>
            Taken at: {DateTime.fromISO(image.createdAt ?? image.updatedAt).toLocaleString(DateTime.DATETIME_MED)}
          </div>
          {showHeroCheckbox && item && <ItemHeroCheckbox imageId={image.id} item={item} />}
        </div>
      </Panel.Footer>
    </Panel>
  </div>
);

const ItemImageGallery: React.FC<{
  title: string;
  images: NonNullable<ItemDetailsQuery['item']>['images'];
  item?: NonNullable<ItemDetailsQuery['item']>;
  showHeroCheckbox?: boolean;
  onSelect(image: Pick<Image, 'id' | 'source' | 'state'>): void;
}> = ({ title, images, item, showHeroCheckbox = false, onSelect }) => {
  if (images.length === 0) return null;

  return (
    <Panel>
      <Panel.Header>
        <Panel.Title>{title}</Panel.Title>
      </Panel.Header>
      <Panel.Body>
        <div className="row">
          {images.map((image, index) => (
            <ItemImagePanel
              key={image.id}
              image={image}
              index={index}
              total={images.length}
              item={item}
              showHeroCheckbox={showHeroCheckbox}
              onSelect={onSelect}
            />
          ))}
        </div>
      </Panel.Body>
    </Panel>
  );
};

const ItemDetailsContent: React.FC<{
  item: NonNullable<ItemDetailsQuery['item']>;
}> = ({ item }) => {
  const [selection, setSelection] = useState<Pick<Image, 'id' | 'source' | 'state'> | undefined>(undefined);

  useTitle(`Item:#${item.barcode.value}`);

  const account = item.account;
  const pallet = item.pallet;
  const group = item.group;

  return (
    <>
      <PageHeader>
        <Roles show={[UserRole.Admin, UserRole.Manager, UserRole.L2Agent]}>
          <span style={{ float: 'right' }}>
            <CreateTechSupportTicket
              ticketable={{
                id: item.id,
                type: Ticket__TicketableEnum.Item,
                displayName: `Item #${item.barcode.value}`,
              }}
            />
          </span>
        </Roles>

        <Breadcrumb>
          <Breadcrumb.Item>
            <NavLink to="/accounts">Accounts</NavLink>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <NavLink to={`/accounts/${account.id}`}>
              #{account.number} ({account.customer.name})
            </NavLink>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <NavLink to={`/accounts/${account.id}/inventory`}>Inventory</NavLink>
          </Breadcrumb.Item>
          <Breadcrumb.Item active>
            <span>#{item.barcode.value}</span>
            {!item.active && (
              <>
                {' '}
                <Label kind="danger">Deleted</Label>
              </>
            )}
          </Breadcrumb.Item>
        </Breadcrumb>
      </PageHeader>

      <Panel>
        <Panel.Header>
          <Panel.Title>Summary</Panel.Title>
        </Panel.Header>
        <Panel.Body>
          <div>
            <strong>Barcode:</strong> <BarcodeValue barcode={item.barcode} />
          </div>

          <div>
            <strong>State:</strong> <ItemState item={item} />
          </div>

          <div>
            <strong>Fragile:</strong> {item.fragile ? 'Yes' : 'No'}
          </div>

          <div>
            <strong>Heavy:</strong> {item.heavy ? 'Yes' : 'No'}
          </div>

          {item.name && (
            <div>
              <strong>Name:</strong> {item.name}
            </div>
          )}

          {item.contents && (
            <div>
              <strong>Contents:</strong> {item.contents}
            </div>
          )}
        </Panel.Body>
        <Panel.Footer align="right">
          <ItemManualReturnButton item={item} />
          <ItemActivateButton item={item} />
          <ItemDeactivateButton item={item} />
        </Panel.Footer>
      </Panel>

      <Panel>
        <Panel.Header>
          <Panel.Title>Details</Panel.Title>
        </Panel.Header>
        <Panel.Body>
          <div>
            <strong>Category: </strong>
            <span>
              <CategoryFormatter resource={item} />
            </span>
            {group && (
              <span>
                <span> disassembled from </span>
                <span>
                  <CategoryFormatter resource={group} />
                </span>
              </span>
            )}
          </div>
          <div>
            <strong>Included Parts:</strong>
            {item.includedParts.map((includedPart, index) => (
              <span key={includedPart.id}>
                {index > 0 ? ', ' : ''} {includedPart.category.name}
              </span>
            ))}
          </div>
          {item.group && (
            <div>
              <strong>DNE Parts For Group:</strong>
              <NonexistentParts itemGroupID={item.group.id} />
            </div>
          )}
          <div>
            {pallet && (
              <div>
                <strong>Pallet: </strong>
                <a href={`/pallets/${pallet.id}`}>
                  #{pallet.barcode.value} ({pallet.state})
                </a>
                <QaQrCodeLink value={pallet.barcode.value} />
                {pallet.location && (
                  <span>
                    <span> - </span>
                    <a href={`/locations/${pallet.location.id}`} className="text-primary">
                      #{pallet.location.value} ({pallet.location.warehouse.name})
                    </a>
                    <QaQrCodeLink value={pallet.location.value} />
                  </span>
                )}
              </div>
            )}
          </div>
        </Panel.Body>
      </Panel>

      {selection && <Lightbox imageURL={selection.source} onClose={() => setSelection(undefined)} />}

      <ItemImageGallery
        title="Customer photos"
        images={item.images}
        item={item}
        showHeroCheckbox
        onSelect={setSelection}
      />

      <ItemImageGallery
        title="Pre-load condition photos"
        images={item.itemPreloadConditionImages}
        onSelect={setSelection}
      />

      <Panel>
        <Panel.Header>
          <Panel.Title>Orders</Panel.Title>
        </Panel.Header>
        <Panel.Body>
          <OrdersList id={item.id} type={OrderSubject.Item} />
        </Panel.Body>
      </Panel>

      <IncidentsPanel kind="item" parentType="items" parentID={item.id} accountID={account.id} timezone={account.tz} />

      <ItemExceptionsPanel itemId={item.id} timezone={account.tz} />

      <TimelineActivityPanel resource={{ id: Number(item.id), type: 'items' }} timezone={account.tz} />

      {item.group && <ItemGroupPanel id={item.group.id} />}

      <AttachedItems itemGroupID={item.group?.id} accountID={account.id} timezone={account.tz} />

      <HistoriesPanel type={History__Subject.Item} id={item.id} timezone={account.tz} />

      <NotesPanel type={NoteNotableEnum.Item} id={item.id} />

      <OpsAuditResultsPanel resourceType="Item" resourceID={item.id} />
    </>
  );
};

export const ItemDetails: React.FC = () => {
  useTitle('Item');
  const { accountID, id } = useParams<{ accountID: string; id: string }>();
  const { data, loading } = useItemDetailsQuery({
    client,
    variables: { accountID, id },
  });

  if (loading) return <Spinner />;

  const item = data?.item;
  if (!item) return null;

  return <ItemDetailsContent item={item} />;
};
